<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">About</span>
      <h1>About the Project</h1>
      <p>
        Steam tells you how many hours you have in each game in total, and roughly what you played in the last two
        weeks. It never tells you what you played on a given day. steamplayti.me fills that gap: it records your
        library once a day and shows the difference as a daily history, game by game.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Look up a Steam profile</router-link>
        <a class="seo-secondary-link" href="#contact">Contact →</a>
      </div>
    </section>

    <section class="seo-section">
      <h2>How the numbers are made</h2>
      <p>
        Every night at 00:00 UTC we ask the Steam Web API for each tracked profile's total playtime per game. The
        difference between two nights is what you played that day. The dashboard, charts, streaks and weekly
        emails are all built from those daily differences.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Never more than 24 hours</h3>
          <p>No single game can show more than 24 hours on one day. If something looks impossible, we would rather show nothing than a wrong number.</p>
        </div>
        <div class="seo-card">
          <h3>Honest about the unknown</h3>
          <p>Hours you played before tracking started cannot be placed on a day, so they count toward your lifetime total but not toward any single day.</p>
        </div>
        <div class="seo-card">
          <h3>Matches Steam</h3>
          <p>Lifetime hours come straight from Steam's own total, so your all-time figure always agrees with your Steam profile.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>What you need</h2>
      <p>
        Only a public Steam profile with public game details. You sign in through Steam's own login page, so we
        never see your password, and nothing needs to be installed. Achievements and store genres are added on top,
        so you can see what kind of games you have been playing and what you unlocked along the way.
      </p>
    </section>

    <section class="seo-section">
      <h2>Who runs it</h2>
      <p>
        steamplayti.me is an independent project built and run by
        <a href="http://steamcommunity.com/profiles/76561198208735799" target="_blank" rel="noopener noreferrer">katates</a>,
        a Steam player who wanted to know where the hours actually went. It is not affiliated with, endorsed by or
        sponsored by Valve Corporation or Steam. Steam and the Steam logo are trademarks of Valve Corporation.
      </p>
      <p>
        The site is free. Ads and
        <a href="https://www.patreon.com/cw/katates?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink" target="_blank" rel="noopener noreferrer">Patreon</a>
        support help cover the servers.
      </p>
    </section>

    <section class="seo-section" id="contact">
      <h2>Contact</h2>
      <p>
        Questions, bug reports, feature ideas or a request to delete your data: email
        <a href="mailto:hello@steamplayti.me">hello@steamplayti.me</a>. Include your Steam profile link if it is about your
        own dashboard. How your data is handled is described in the
        <router-link to="/privacy/">Privacy Policy</router-link>.
      </p>
    </section>
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';

const CANONICAL = 'https://steamplayti.me/about/';
const META_DESCRIPTION = 'What steamplayti.me is, how it turns Steam playtime into a daily history, who runs it, and how to get in touch.';

const META = {
  title: 'About & Contact | steamplayti.me',
  description: META_DESCRIPTION,
  canonical: CANONICAL,
  ogTitle: 'About steamplayti.me',
  ogDescription: META_DESCRIPTION,
  ogUrl: CANONICAL,
  twitterTitle: 'About steamplayti.me',
  twitterDescription: META_DESCRIPTION,
  structuredData: pageStructuredData({
    name: 'About steamplayti.me',
    description: META_DESCRIPTION,
    canonical: CANONICAL,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'About', path: CANONICAL },
    ],
  }),
};

export default {
  name: 'AboutPage',
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
<style scoped>
.seo-section a {
  color: var(--primary);
}
</style>
